/* Pexeso name + shared leaderboard.

   - The guest name (USER_KEY) is purely per-browser, no backend involvement.
   - The leaderboard is fetched from /api/pexeso/leaderboard and cached in
     localStorage for instant first paint and offline tolerance.
   - On first run we one-shot push any pre-existing localStorage history
     (LEGACY_LOCAL_BOARD_KEY, v2 from the local-only era) to the backend
     so the migrated guest's old scores still appear in the shared ranking. */

import {
  fetchPexesoLeaderboard,
  migratePexesoLocal,
  submitPexesoAttempt,
  type PexesoMigrateEntry,
  type PexesoScoreDto,
} from './api';

const USER_KEY = 'wedding.user.v1';
/** Cache of the last fetched shared leaderboard, for instant first paint. */
const SHARED_CACHE_KEY = 'wedding.leaderboard.shared.v1';
/** Legacy per-browser leaderboard (v2 of the pre-backend era). Read once
    for migration, then cleared. */
const LEGACY_LOCAL_BOARD_KEY = 'wedding.leaderboard.v2';
/** Set after a successful migration push so we never duplicate-push. */
const MIGRATED_FLAG_KEY = 'wedding.leaderboard.migrated.v1';
/** Even older v1 per-attempt log — we never migrated it (names there were
    unconstrained). Cleared on first call. */
const ANCIENT_LEGACY_KEY = 'wedding.leaderboard.v1';

const MAX_NAME_LEN = 32;

export type PexesoUser = {
  name: string;
  createdAt: string;
};

/** Aggregate of a single named guest across all of their plays. */
export type PexesoPlayer = {
  name: string;
  attempts: number;
  firstClicks: number | null;
  bestClicks: number | null;
  skipCount: number;
  firstPlayedAt: string;
  lastPlayedAt: string;
};

function safeGet<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function safeSet(key: string, value: unknown) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota errors — cache is best-effort */
  }
}

function safeRemove(key: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function dtoToPlayer(dto: PexesoScoreDto): PexesoPlayer {
  return { ...dto };
}

export function loadUser(): PexesoUser | null {
  return safeGet<PexesoUser | null>(USER_KEY, null);
}

export function saveUser(name: string): PexesoUser {
  const user: PexesoUser = {
    name: name.trim().slice(0, MAX_NAME_LEN),
    createdAt: new Date().toISOString(),
  };
  safeSet(USER_KEY, user);
  return user;
}

/** Instant-paint cache of the last leaderboard the server sent us. */
export function loadLeaderboardCache(): PexesoPlayer[] {
  return safeGet<PexesoPlayer[]>(SHARED_CACHE_KEY, []);
}

function writeCache(board: PexesoPlayer[]) {
  safeSet(SHARED_CACHE_KEY, board);
}

/** Result of {@link initLeaderboard}. `migrated` is true on the one render
    where the local-era history was just pushed up — Hero uses it to show
    a "your old scores were added" notice. */
export type InitResult = {
  board: PexesoPlayer[];
  migrated: boolean;
};

/** Called once on mount. Runs the one-shot migration if any legacy local
    data exists, otherwise just fetches the current shared board. Falls
    back to the cached snapshot on network failure. */
export async function initLeaderboard(): Promise<InitResult> {
  /* Drop the ancient v1 per-attempt log unconditionally — we never had
     a migration path for it. */
  safeRemove(ANCIENT_LEGACY_KEY);

  const alreadyMigrated = safeGet<boolean>(MIGRATED_FLAG_KEY, false);
  const legacy = safeGet<PexesoPlayer[]>(LEGACY_LOCAL_BOARD_KEY, []);
  const shouldMigrate = !alreadyMigrated && legacy.length > 0;

  try {
    if (shouldMigrate) {
      const entries: PexesoMigrateEntry[] = legacy.map((p) => ({
        name: p.name,
        attempts: p.attempts,
        firstClicks: p.firstClicks,
        bestClicks: p.bestClicks,
        skipCount: p.skipCount,
        firstPlayedAt: p.firstPlayedAt,
        lastPlayedAt: p.lastPlayedAt,
      }));
      const dtos = await migratePexesoLocal(entries);
      safeSet(MIGRATED_FLAG_KEY, true);
      safeRemove(LEGACY_LOCAL_BOARD_KEY);
      const board = dtos.map(dtoToPlayer);
      writeCache(board);
      return { board, migrated: true };
    }

    const dtos = await fetchPexesoLeaderboard();
    const board = dtos.map(dtoToPlayer);
    writeCache(board);
    return { board, migrated: false };
  } catch {
    /* Offline / server down — render the last cached snapshot. The next
       successful attempt or page load will refresh it. */
    return { board: loadLeaderboardCache(), migrated: false };
  }
}

/** Post one game outcome to the server and refresh the local cache.
    Falls back to the cached board on network failure so the UI still
    has something to render. */
export async function submitAttempt(
  name: string,
  clicks: number,
  skipped: boolean,
): Promise<PexesoPlayer[]> {
  const trimmed = name.trim().slice(0, MAX_NAME_LEN);
  if (!trimmed) return loadLeaderboardCache();

  try {
    await submitPexesoAttempt(trimmed, clicks, skipped);
    const dtos = await fetchPexesoLeaderboard();
    const board = dtos.map(dtoToPlayer);
    writeCache(board);
    return board;
  } catch {
    return loadLeaderboardCache();
  }
}

/** Winners (at least one completed game) ordered by best-clicks asc,
    then always-skippers at the bottom ordered by most recent. */
export function rankPlayers(list: PexesoPlayer[]): PexesoPlayer[] {
  const winners = list
    .filter((p) => p.bestClicks != null)
    .sort(
      (a, b) =>
        (a.bestClicks ?? Infinity) - (b.bestClicks ?? Infinity) ||
        a.firstPlayedAt.localeCompare(b.firstPlayedAt),
    );
  const skippers = list
    .filter((p) => p.bestClicks == null)
    .sort((a, b) => b.lastPlayedAt.localeCompare(a.lastPlayedAt));
  return [...winners, ...skippers];
}
